'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ScientificCalculator() {
  const t = useTranslations("tools.scientificCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [currentInput, setCurrentInput] = useState('');
  const [displayInput, setDisplayInput] = useState('');
  const [displayOutput, setDisplayOutput] = useState('0');
  const [memory, setMemory] = useState(0);
  const [angleMode, setAngleMode] = useState<'deg' | 'rad'>('deg');
  const [history, setHistory] = useState<Array<{ expression: string; result: string }>>([]);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '3rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
      { fontSize: '1rem', maxWidth: '100%' },
      { fontSize: '1.125rem', maxWidth: '700px' },
      { fontSize: '1.25rem', maxWidth: '800px' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto' },
      { maxWidth: '100%' },
      { maxWidth: '600px' },
      { maxWidth: '600px' }
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    toolTitle: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 700, margin: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorBody: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    displayContainer: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '1rem',
        marginBottom: '1.5rem',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-end',
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    displayInput: {
      color: '#94a3b8',
      fontSize: '1rem',
      minHeight: '1.5rem',
      wordWrap: 'break-word' as const,
      marginBottom: '0.5rem',
      fontFamily: "'Courier New', monospace",
    },
    displayOutput: getResponsiveStyle(
      {
        color: '#ffffff',
        fontWeight: 600,
        textAlign: 'right' as const,
        wordWrap: 'break-word' as const,
        fontFamily: "'Courier New', monospace",
      },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.5rem' }
    ),
    modeSelector: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: '#f1f5f9',
      borderRadius: '0.75rem',
    },
    modeLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      color: '#475569',
      fontWeight: 500,
    },
    buttonGrid: {
      display: 'grid',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    functionRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '0.5rem',
    },
    calcBtn: getResponsiveStyle(
      {
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontFamily: 'inherit',
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    btnFunction: {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      color: '#0369a1',
    },
    btnNumber: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      color: '#1e293b',
    },
    btnOperator: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      color: '#92400e',
    },
    btnEquals: {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
    },
    btnClear: {
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: 'white',
    },
    btnMemory: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #c084fc 0%, #a855f7 100%)',
        color: 'white',
      },
      { fontSize: '0.8rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    btnMemoryActive: {
      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
      boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.3)',
    },
    historyContainer: {
      marginTop: '1.5rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.75rem',
      maxHeight: '200px',
      overflowY: 'auto' as const,
    },
    historyTitle: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.5rem',
    },
    historyItem: {
      padding: '0.5rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.5rem',
      fontSize: '0.85rem',
      color: '#64748b',
      fontFamily: "'Courier New', monospace",
    },
    historyEmpty: {
      textAlign: 'center' as const,
      color: '#94a3b8',
      fontSize: '0.85rem',
      padding: '1rem',
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem', marginBottom: '1.25rem' },
      { fontSize: '2rem', marginBottom: '1.5rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' }
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
  };

  const factorial = (n: number): number => {
    if (n < 0) return NaN;
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) < 1e-10 && num !== 0) {
      return num.toExponential(6);
    }
    if (Math.abs(num) > 1e10) {
      return num.toExponential(6);
    }
    const str = num.toString();
    if (str.length > 12) {
      return parseFloat(num.toPrecision(10)).toString();
    }
    return str;
  };

  const addToHistory = (expression: string, result: number) => {
    const newHistory = [
      { expression, result: formatNumber(result) },
      ...history.slice(0, 9)
    ];
    setHistory(newHistory);
  };

  const updateDisplay = (input: string, display: string) => {
    setDisplayInput(display);

    if (input) {
      try {
        const expression = input.replace(/\*\*/g, '^');
        const lastNum = expression.split(/[\+\-\*\/\(\)]/).pop() || '';
        const num = parseFloat(lastNum);
        if (!isNaN(num)) {
          setDisplayOutput(formatNumber(num));
        } else {
          setDisplayOutput(input || '0');
        }
      } catch (e) {
        setDisplayOutput(input || '0');
      }
    } else {
      setDisplayOutput('0');
    }
  };

  const handleFunction = (func: string) => {
    const val = parseFloat(displayOutput);
    if (isNaN(val)) return;

    let result: number = 0;
    let displayFunc: string = '';

    switch(func) {
      case 'sin':
        result = angleMode === 'deg' ? Math.sin(val * Math.PI / 180) : Math.sin(val);
        displayFunc = `sin(${val})`;
        break;
      case 'cos':
        result = angleMode === 'deg' ? Math.cos(val * Math.PI / 180) : Math.cos(val);
        displayFunc = `cos(${val})`;
        break;
      case 'tan':
        result = angleMode === 'deg' ? Math.tan(val * Math.PI / 180) : Math.tan(val);
        displayFunc = `tan(${val})`;
        break;
      case 'asin':
        result = Math.asin(val);
        result = angleMode === 'deg' ? result * 180 / Math.PI : result;
        displayFunc = `sin⁻¹(${val})`;
        break;
      case 'acos':
        result = Math.acos(val);
        result = angleMode === 'deg' ? result * 180 / Math.PI : result;
        displayFunc = `cos⁻¹(${val})`;
        break;
      case 'atan':
        result = Math.atan(val);
        result = angleMode === 'deg' ? result * 180 / Math.PI : result;
        displayFunc = `tan⁻¹(${val})`;
        break;
      case 'ln':
        result = Math.log(val);
        displayFunc = `ln(${val})`;
        break;
      case 'log':
        result = Math.log10(val);
        displayFunc = `log(${val})`;
        break;
      case 'sqrt':
        result = Math.sqrt(val);
        displayFunc = `√(${val})`;
        break;
      case 'cbrt':
        result = Math.cbrt(val);
        displayFunc = `∛(${val})`;
        break;
      case 'x2':
        result = val * val;
        displayFunc = `${val}²`;
        break;
      case 'x3':
        result = val * val * val;
        displayFunc = `${val}³`;
        break;
      case 'ex':
        result = Math.exp(val);
        displayFunc = `e^${val}`;
        break;
      case '10x':
        result = Math.pow(10, val);
        displayFunc = `10^${val}`;
        break;
      case '1/x':
        result = 1 / val;
        displayFunc = `1/${val}`;
        break;
      case 'factorial':
        result = factorial(Math.floor(val));
        displayFunc = `${Math.floor(val)}!`;
        break;
      case 'percent':
        result = val / 100;
        displayFunc = `${val}%`;
        break;
      case 'pow':
        const newInput = currentInput + '**';
        const newDisplay = displayInput + '^';
        setCurrentInput(newInput);
        updateDisplay(newInput, newDisplay);
        return;
      default:
        return;
    }

    if (!isNaN(result) && isFinite(result)) {
      addToHistory(displayFunc, result);
      setCurrentInput(result.toString());
      setDisplayInput(result.toString());
      setDisplayOutput(formatNumber(result));
    }
  };

  // 安全的数学表达式解析器，替代 eval()
  const safeEvaluate = (expr: string): number => {
    // 移除空格
    expr = expr.replace(/\s/g, '');

    // 检查表达式是否只包含合法字符
    if (!/^[\d+\-*/().e]+$/i.test(expr)) {
      throw new Error('Invalid expression');
    }

    // 使用 Function 构造器来安全地计算数学表达式
    // 这比 eval 更安全，因为它在独立的作用域中运行
    const calculate = new Function('return ' + expr);
    return calculate();
  };

  const calculate = () => {
    if (!currentInput) return;

    try {
      let expression = currentInput.replace(/\^/g, '**');
      expression = expression.replace(/×/g, '*');
      expression = expression.replace(/÷/g, '/');

      const result = safeEvaluate(expression);

      if (!isNaN(result) && isFinite(result)) {
        addToHistory(displayInput, result);
        setCurrentInput(result.toString());
        setDisplayInput('');
        setDisplayOutput(formatNumber(result));
        setWaitingForOperand(true);
      } else {
        setDisplayOutput('Error');
      }
    } catch (e) {
      setDisplayOutput('Error');
    }
  };

  const handleInput = (action: string) => {
    if (action >= '0' && action <= '9') {
      if (waitingForOperand) {
        setCurrentInput(action);
        updateDisplay(action, action);
        setWaitingForOperand(false);
      } else {
        const newInput = currentInput + action;
        const newDisplay = displayInput + action;
        setCurrentInput(newInput);
        updateDisplay(newInput, newDisplay);
      }
    } else if (action === '.') {
      if (waitingForOperand) {
        setCurrentInput('0.');
        updateDisplay('0.', '0.');
        setWaitingForOperand(false);
      } else if (!currentInput.includes('.')) {
        const newInput = currentInput + '.';
        const newDisplay = displayInput + '.';
        setCurrentInput(newInput);
        updateDisplay(newInput, newDisplay);
      }
    } else if (['+', '-', '*', '/'].includes(action)) {
      if (currentInput) {
        const newInput = currentInput + action;
        const newDisplay = displayInput + ' ' + action + ' ';
        setCurrentInput(newInput);
        updateDisplay(newInput, newDisplay);
        setWaitingForOperand(true);
      }
    } else if (action === '(' || action === ')') {
      const newInput = currentInput + action;
      const newDisplay = displayInput + action;
      setCurrentInput(newInput);
      updateDisplay(newInput, newDisplay);
    } else if (action === 'back') {
      if (currentInput.length > 0) {
        const newInput = currentInput.slice(0, -1);
        const newDisplay = displayInput.slice(0, -1);
        setCurrentInput(newInput);
        updateDisplay(newInput, newDisplay);
      }
    } else if (action === 'C') {
      setCurrentInput('');
      updateDisplay('', '');
    } else if (action === '=') {
      calculate();
    } else if (action === 'M+') {
      const val = parseFloat(displayOutput);
      if (!isNaN(val)) {
        setMemory(memory + val);
      }
    } else if (action === 'M-') {
      const val = parseFloat(displayOutput);
      if (!isNaN(val)) {
        setMemory(memory - val);
      }
    } else if (action === 'MR') {
      setCurrentInput(memory.toString());
      updateDisplay(memory.toString(), memory.toString());
    } else if (action === '+/-') {
      if (currentInput) {
        const val = parseFloat(displayOutput);
        if (!isNaN(val)) {
          const newVal = -val;
          setCurrentInput(newVal.toString());
          updateDisplay(newVal.toString(), newVal.toString());
        }
      }
    } else if (action === 'rnd') {
      const rnd = Math.random();
      setCurrentInput(rnd.toString());
      updateDisplay(rnd.toString(), rnd.toString());
    } else if (action === 'pi') {
      const newInput = currentInput + Math.PI.toString();
      const newDisplay = displayInput + 'π';
      setCurrentInput(newInput);
      updateDisplay(newInput, newDisplay);
    } else if (action === 'e') {
      const newInput = currentInput + Math.E.toString();
      const newDisplay = displayInput + 'e';
      setCurrentInput(newInput);
      updateDisplay(newInput, newDisplay);
    } else if (action === 'exp') {
      const newInput = currentInput + 'e';
      const newDisplay = displayInput + 'E';
      setCurrentInput(newInput);
      updateDisplay(newInput, newDisplay);
    } else {
      handleFunction(action);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleInput(e.key);
      } else if (e.key === '.') {
        handleInput('.');
      } else if (['+', '-', '*', '/'].includes(e.key)) {
        handleInput(e.key);
      } else if (e.key === 'Enter' || e.key === '=') {
        e.preventDefault();
        calculate();
      } else if (e.key === 'Backspace') {
        handleInput('back');
      } else if (e.key === 'Escape') {
        handleInput('C');
      } else if (e.key === '(' || e.key === ')') {
        handleInput(e.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentInput, displayInput, displayOutput, waitingForOperand]);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolTitle}>🧮 {t("toolTitle")}</h2>
          </div>

          <div style={styles.calculatorBody}>
            <div style={styles.displayContainer}>
              <div style={styles.displayInput}>{displayInput}</div>
              <div style={styles.displayOutput}>{displayOutput}</div>
            </div>

            <div style={styles.modeSelector}>
              <label style={styles.modeLabel}>
                <input
                  type="radio"
                  name="angle-mode"
                  value="deg"
                  checked={angleMode === 'deg'}
                  onChange={() => setAngleMode('deg')}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                {t("degrees")}
              </label>
              <label style={styles.modeLabel}>
                <input
                  type="radio"
                  name="angle-mode"
                  value="rad"
                  checked={angleMode === 'rad'}
                  onChange={() => setAngleMode('rad')}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                {t("radians")}
              </label>
            </div>

            <div style={styles.buttonGrid}>
              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('sin')}>sin</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('cos')}>cos</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('tan')}>tan</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('pi')}>π</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('e')}>e</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('asin')}>sin<sup>-1</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('acos')}>cos<sup>-1</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('atan')}>tan<sup>-1</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('ln')}>ln</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('log')}>log</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('pow')}>x<sup>y</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('x3')}>x<sup>3</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('x2')}>x<sup>2</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('ex')}>e<sup>x</sup></button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('10x')}>10<sup>x</sup></button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('sqrt')}>√x</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('cbrt')}><sup>3</sup>√x</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('1/x')}>1/x</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('factorial')}>n!</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('percent')}>%</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('(')}>(</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput(')')}>)</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('exp')}>EXP</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('rnd')}>RND</button>
                <button style={{...styles.calcBtn, ...styles.btnFunction}} onClick={() => handleInput('+/-')}>±</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('7')}>7</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('8')}>8</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('9')}>9</button>
                <button style={{...styles.calcBtn, ...styles.btnOperator}} onClick={() => handleInput('/')}>÷</button>
                <button style={{...styles.calcBtn, ...styles.btnOperator}} onClick={() => handleInput('back')}>{t("back")}</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('4')}>4</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('5')}>5</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('6')}>6</button>
                <button style={{...styles.calcBtn, ...styles.btnOperator}} onClick={() => handleInput('*')}>×</button>
                <button style={{...styles.calcBtn, ...styles.btnMemory}} onClick={() => handleInput('M+')}>M+</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('1')}>1</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('2')}>2</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('3')}>3</button>
                <button style={{...styles.calcBtn, ...styles.btnOperator}} onClick={() => handleInput('-')}>−</button>
                <button style={{...styles.calcBtn, ...styles.btnMemory}} onClick={() => handleInput('M-')}>M−</button>
              </div>

              <div style={styles.functionRow}>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('0')}>0</button>
                <button style={{...styles.calcBtn, ...styles.btnNumber}} onClick={() => handleInput('.')}>.</button>
                <button style={{...styles.calcBtn, ...styles.btnOperator}} onClick={() => handleInput('+')}>+</button>
                <button style={{...styles.calcBtn, ...styles.btnClear}} onClick={() => handleInput('C')}>AC</button>
                <button
                  style={{
                    ...styles.calcBtn,
                    ...styles.btnMemory,
                    ...(memory !== 0 ? styles.btnMemoryActive : {})
                  }}
                  onClick={() => handleInput('MR')}
                >
                  MR
                </button>
              </div>

              <div style={styles.functionRow}>
                <button
                  style={{...styles.calcBtn, ...styles.btnEquals, gridColumn: 'span 5'}}
                  onClick={() => handleInput('=')}
                >
                  =
                </button>
              </div>
            </div>

            <div style={styles.historyContainer}>
              <div style={styles.historyTitle}>{t("history")}</div>
              <div>
                {history.length === 0 ? (
                  <div style={styles.historyEmpty}>{t("noCalculations")}</div>
                ) : (
                  history.map((item, index) => (
                    <div key={index} style={styles.historyItem}>
                      {item.expression} = {item.result}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li>{t("howToStep1")}</li>
          <li>{t("howToStep2")}</li>
          <li>{t("howToStep3")}</li>
          <li>{t("howToStep4")}</li>
          <li>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("featuresTitle")}</h2>

        <h3 style={styles.h3}>{t("basicOperationsTitle")}</h3>
        <p style={styles.paragraph}>{t("basicOperationsDesc")}</p>

        <h3 style={styles.h3}>{t("trigonometryTitle")}</h3>
        <p style={styles.paragraph}>{t("trigonometryDesc")}</p>

        <h3 style={styles.h3}>{t("logarithmsTitle")}</h3>
        <p style={styles.paragraph}>{t("logarithmsDesc")}</p>

        <h3 style={styles.h3}>{t("exponentsTitle")}</h3>
        <p style={styles.paragraph}>{t("exponentsDesc")}</p>

        <h3 style={styles.h3}>{t("memoryTitle")}</h3>
        <p style={styles.paragraph}>{t("memoryDesc")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>
        <p style={styles.paragraph}>{t("insightsParagraph2")}</p>
        <p style={styles.paragraph}>{t("insightsParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("applicationsTitle")}</h2>
        <ul style={styles.list}>
          <li>{t("application1")}</li>
          <li>{t("application2")}</li>
          <li>{t("application3")}</li>
          <li>{t("application4")}</li>
          <li>{t("application5")}</li>
          <li>{t("application6")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.paragraph}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.paragraph}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.paragraph}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.paragraph}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.paragraph}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ol style={styles.list}>
          <li>
            <a
              href="https://www.ntnu.edu/cerg/vo2max"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref1")}
            </a>
          </li>
          <li>
            <a
              href="https://gravitus.com/tools/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref2")}
            </a>
          </li>
          <li>
            <a
              href="https://www.coachwithfitr.com/calculators"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref3")}
            </a>
          </li>
          <li>
            <a
              href="https://welltech.com/calculators/health-fitness-calculators"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref4")}
            </a>
          </li>
          <li>
            <a
              href="https://acecalculator.com/best-calculator-app-2025-smart-fast-free-tools-for-every-device/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref5")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}